// 화면 19 — Stock Score Explorer (Phase C) 메인 페이지.
// 라우트: /leading/scores
// 권위: ARCH_DASHBOARD §4.19 / §6.2 (v1.10.3), PRD_Dashboard §5.9.
// 폴링 300s. WebSocket 미사용 (ADR-DASH-06).
//
// 레이아웃 (위→아래):
//   - 헤더 + 종목 검색 autocomplete
//   - 선택 종목 90일 score 시계열 (final_score line + Top45 진입 마커 + band strip)
//   - 시장 전체 밴드 분포 누적 막대 (N 토글 30/60/90, default 60)
//   - 단일 일자 percentile 히스토그램 (일자 선택기 + bins=10)
//
// URL 동기화:
//   ?symbol={006자종목코드}   — 선택 종목
//   ?distribution_days={30|60|90} — 누적 막대 lookback
//   ?date={YYYY-MM-DD} — percentile 일자 (미지정 시 서버 max(trading_date) 폴백)

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ApiError } from '../../api/client';
import { StockSearchAutocomplete } from './StockSearchAutocomplete';
import {
  ScoreHistoryChart,
  ScoreHistoryChartLegend,
} from './ScoreHistoryChart';
import {
  BandDistributionChart,
  BandDistributionLegend,
} from './BandDistributionChart';
import { PercentileHistogramChart } from './PercentileHistogram';
import {
  useBandsDistribution,
  usePercentileHistogram,
  useScoreHistory,
} from './useStockScoreExplorer';
import {
  BAND_DISTRIBUTION_DAYS,
  SCORE_HISTORY_DAYS,
  type BandDistributionDays,
} from './types';
import './stockScoreExplorer.css';

function parseDays(v: string | null): BandDistributionDays {
  const n = Number(v);
  if ((BAND_DISTRIBUTION_DAYS as readonly number[]).includes(n)) {
    return n as BandDistributionDays;
  }
  return 60;
}

const SYMBOL_RE = /^\d{6}$/;
function parseSymbol(v: string | null): string | null {
  if (!v) return null;
  return SYMBOL_RE.test(v) ? v : null;
}

export default function StockScoreExplorerPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedSymbol = parseSymbol(searchParams.get('symbol'));
  const distributionDays = parseDays(searchParams.get('distribution_days'));
  const percentileDate = searchParams.get('date');

  const setSymbol = useCallback(
    (s: string) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('symbol', s);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setDistributionDays = useCallback(
    (d: BandDistributionDays) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('distribution_days', String(d));
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setPercentileDate = useCallback(
    (d: string) => {
      const sp = new URLSearchParams(searchParams);
      if (d) sp.set('date', d);
      else sp.delete('date');
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // 데이터 훅.
  const scoreHistory = useScoreHistory(selectedSymbol, SCORE_HISTORY_DAYS);
  const bandsDist = useBandsDistribution(distributionDays);
  const percentile = usePercentileHistogram(percentileDate, 10);

  const isFetching =
    scoreHistory.isFetching || bandsDist.isFetching || percentile.isFetching;

  const scoreErr = scoreHistory.isError
    ? (scoreHistory.error as ApiError)
    : null;
  const bandsErr = bandsDist.isError ? (bandsDist.error as ApiError) : null;
  const pctErr = percentile.isError ? (percentile.error as ApiError) : null;

  // 진입 마커 카운트 (90일 중 in_top45=true 일자 수).
  const inTop45Count = useMemo(
    () => scoreHistory.data?.rows.filter((r) => r.in_top45).length ?? 0,
    [scoreHistory.data]
  );

  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  }, []);

  return (
    <div className="sse-page" data-testid="stock-score-explorer-page">
      <header className="sse-page__header">
        <div className="sse-page__title-wrap">
          <h1 className="sse-page__title">Stock Score Explorer</h1>
          <span className="sse-muted">
            Phase C · 90일 score 시계열 + 시장 분포 + percentile 히스토그램
            {isFetching && ' · 갱신 중…'}
          </span>
        </div>
      </header>

      {/* 종목 검색 */}
      <section className="sse-panel" aria-labelledby="sse-search-h">
        <div className="sse-panel__header">
          <h2 className="sse-panel__title" id="sse-search-h">
            종목 검색
          </h2>
          <span className="sse-panel__hint">
            symbol prefix / name prefix / name LIKE · cap 20 · 폴링 없음 (debounce 300ms)
          </span>
        </div>
        <StockSearchAutocomplete
          selectedSymbol={selectedSymbol}
          onSelect={setSymbol}
        />
      </section>

      {/* 90일 score 시계열 (선택 종목) */}
      <section className="sse-panel" aria-labelledby="sse-score-h">
        <div className="sse-panel__header">
          <h2 className="sse-panel__title" id="sse-score-h">
            90일 score 시계열
          </h2>
          <span className="sse-panel__hint">
            {selectedSymbol
              ? scoreHistory.data
                ? `${scoreHistory.data.name} (${selectedSymbol}) · sector: ${
                    scoreHistory.data.sector ?? '-'
                  } · ${inTop45Count}건 Top45 진입 / ${scoreHistory.data.rows.length} row`
                : '…'
              : '종목을 검색해주세요.'}
          </span>
        </div>
        {!selectedSymbol && (
          <div className="sse-empty">
            상단 검색창에서 종목을 선택하면 90일 score 시계열을 표시합니다.
          </div>
        )}
        {selectedSymbol && scoreErr && (
          <div className="sse-banner sse-banner--error" data-testid="sse-score-error">
            {scoreErr.httpStatus === 404
              ? `${selectedSymbol} 종목 데이터가 없습니다 (미존재 또는 미적재).`
              : `score-history 로드 실패: ${scoreErr.message}`}
          </div>
        )}
        {selectedSymbol && scoreHistory.isLoading && !scoreHistory.data && (
          <div className="sse-empty">불러오는 중…</div>
        )}
        {selectedSymbol &&
          scoreHistory.data &&
          scoreHistory.data.rows.length === 0 && (
            <div className="sse-empty">
              최근 {SCORE_HISTORY_DAYS}거래일 stock_daily_score row 없음.
            </div>
          )}
        {selectedSymbol &&
          scoreHistory.data &&
          scoreHistory.data.rows.length > 0 && (
            <>
              <ScoreHistoryChart
                rows={scoreHistory.data.rows}
                sector={scoreHistory.data.sector}
              />
              <ScoreHistoryChartLegend />
            </>
          )}
      </section>

      {/* 시장 전체 밴드 분포 누적 막대 */}
      <section className="sse-panel" aria-labelledby="sse-bands-h">
        <div className="sse-panel__header">
          <h2 className="sse-panel__title" id="sse-bands-h">
            시장 전체 밴드 분포
          </h2>
          <span className="sse-panel__hint">
            {bandsDist.data
              ? `${bandsDist.data.rows.length} 일자`
              : '…'}{' '}
            · stock_daily_score GROUP BY date, band
          </span>
        </div>
        <div className="sse-toolbar">
          <span className="sse-toolbar__label">lookback</span>
          <div className="sse-toggle" role="group" aria-label="lookback days">
            {BAND_DISTRIBUTION_DAYS.map((d) => (
              <button
                key={d}
                type="button"
                className={
                  'sse-toggle__btn' +
                  (d === distributionDays ? ' sse-toggle__btn--active' : '')
                }
                onClick={() => setDistributionDays(d)}
                aria-pressed={d === distributionDays}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        {bandsErr && (
          <div className="sse-banner sse-banner--error">
            band-distribution 로드 실패: {bandsErr.message}
          </div>
        )}
        {bandsDist.isLoading && !bandsDist.data && (
          <div className="sse-empty">불러오는 중…</div>
        )}
        {bandsDist.data && bandsDist.data.rows.length === 0 && (
          <div className="sse-empty">
            최근 {distributionDays}거래일 적재 row 없음.
          </div>
        )}
        {bandsDist.data && bandsDist.data.rows.length > 0 && (
          <>
            <BandDistributionChart rows={bandsDist.data.rows} />
            <BandDistributionLegend />
          </>
        )}
      </section>

      {/* 단일 일자 percentile 히스토그램 */}
      <section className="sse-panel" aria-labelledby="sse-pct-h">
        <div className="sse-panel__header">
          <h2 className="sse-panel__title" id="sse-pct-h">
            percentile 히스토그램
          </h2>
          <span className="sse-panel__hint">
            {percentile.data
              ? `${percentile.data.trading_date} · ${percentile.data.total_count} 종목 (universe size) · bins=${percentile.data.bins}`
              : '…'}
          </span>
        </div>
        <div className="sse-toolbar">
          <label className="sse-toolbar__label" htmlFor="sse-pct-date">
            일자
          </label>
          <input
            id="sse-pct-date"
            type="date"
            className="sse-toolbar__input"
            value={percentileDate ?? percentile.data?.trading_date ?? ''}
            max={todayStr}
            onChange={(e) => setPercentileDate(e.target.value)}
            data-testid="sse-pct-date"
          />
          <span className="sse-muted">· 미지정 시 최근 일자</span>
        </div>
        {pctErr && (
          <div className="sse-banner sse-banner--error">
            {pctErr.httpStatus === 404
              ? `${percentileDate ?? '최근'} 일자 row 없음 (휴장 또는 미적재).`
              : `percentile 로드 실패: ${pctErr.message}`}
          </div>
        )}
        {percentile.isLoading && !percentile.data && (
          <div className="sse-empty">불러오는 중…</div>
        )}
        {percentile.data && percentile.data.histogram.length > 0 && (
          <PercentileHistogramChart
            bins={percentile.data.histogram}
            totalCount={percentile.data.total_count}
          />
        )}
        {percentile.data && percentile.data.histogram.length === 0 && (
          <div className="sse-empty">히스토그램 bin 없음.</div>
        )}
      </section>
    </div>
  );
}
